import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/CustomerSubscriptionsService';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useCustomerSubscriptions = (context: 'customer' | 'reports' = 'customer') => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    const { id: customerId } = useParams();

    const sort = { column: 'created', direction: 'desc' as const };
    const filter = (context === 'customer' && customerId) ? { customerId } : undefined;

    // INFINITE QUERY
    const customerSubscriptionsQuery = useInfiniteQuery({
        queryKey: ['customer-subscriptions', keyword, limit, sort.column, sort.direction, customerId],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, filter, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Customer Subscription deleted');
            queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
        },
        onError: notifyError
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = customerSubscriptionsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: customerSubscriptionsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: customerSubscriptionsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
